/**
 * Retry a write that is safe to repeat (it carries a clientEventId the server
 * deduplicates on). Network errors, 5xx and 429 are retried with exponential
 * backoff; any other 4xx is final (the server refused the write) (AS-B05).
 *
 * @param {() => Promise<any>} write
 * @param {Object} [opts]
 * @param {number} [opts.retries=3]       retries after the first attempt
 * @param {number} [opts.baseDelayMs=400] delay before the first retry (doubles each time)
 * @param {(ms:number) => Promise<void>} [opts.sleep]
 * @returns {Promise<any>} the write's result; rejects with the last error
 */
export const isRetryableError = (err) => {
  const status = err?.response?.status;
  if (!status) return true; // network error / timeout
  return status >= 500 || status === 429;
};

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const retryWrite = async (write, { retries = 3, baseDelayMs = 400, sleep = defaultSleep } = {}) => {
  let attempt = 0;
  for (;;) {
    try {
      return await write();
    } catch (err) {
      if (attempt >= retries || !isRetryableError(err)) throw err;
      await sleep(baseDelayMs * 2 ** attempt);
      attempt += 1;
    }
  }
};

export default retryWrite;
